/**
 * Iwan — a hall that ends in a monumental portal.
 *
 * You walk the length of a plain top-lit hall and the far end is shut by a
 * pishtaq: a rectangular frame of three receding orders, corbelled at the
 * soffit, rising as a blank screen to the ceiling. Through it is a shallow
 * raised alcove with its own low lit soffit, and on its back wall the one
 * picture the whole room is aimed at.
 *
 * role: 'wing'. Long walls cluster; everything inside the portal is a hero.
 */

import { PALACE } from '../constants';
import { rectSegments } from '../hang';

// stone/terracotta are contract materials; fall back on an older kit.
const mat = (kit, name, fallback) => kit.materials[name] || kit.materials[fallback];

const AD = 3.2; // alcove depth behind the portal plane
const PLINTH = 0.12; // dais height — low enough to walk straight onto
const FRAME_T = 0.6; // depth of the portal screen
const ORDERS = 3; // receding orders of the frame
const CREST = 5.5; // top of the outermost frame band
const HALL_H = 8.5;

/**
 * Which short end carries the alcove. Deterministic from the node alone (no
 * rand) so plan(), segments() and build() always agree: prefer the end the
 * generator put fewest doors on.
 */
const alcoveEnd = (node) => {
  const alongX = node.x2 - node.x1 >= node.z2 - node.z1;
  const ends = alongX ? ['E', 'W'] : ['N', 'S'];
  const count = (s) => (node.doors || []).filter((d) => d.side === s).length;
  return count(ends[0]) <= count(ends[1]) ? ends[0] : ends[1];
};

// Inward normal per side, used to tell rectSegments' output apart by face.
const INWARD = { N: [0, -1], S: [0, 1], E: [-1, 0], W: [1, 0] };

const iwan = {
  id: 'iwan',
  role: 'wing',
  weight: 2,

  plan(rand, { axis }) {
    const long = 12 + rand() * 3; // 12–15
    const cross = 9 + rand() * 2; // 9–11
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: HALL_H,
      params: {},
    };
  },

  segments(node) {
    const { WALL_T } = PALACE;
    const end = alcoveEnd(node);
    const alongX = end === 'E' || end === 'W';
    const sgn = end === 'E' || end === 'N' ? 1 : -1;
    const uI1 = alongX ? node.x1 + WALL_T : node.z1 + WALL_T;
    const uI2 = alongX ? node.x2 - WALL_T : node.z2 - WALL_T;
    const vI1 = alongX ? node.z1 + WALL_T : node.x1 + WALL_T;
    const vI2 = alongX ? node.z2 - WALL_T : node.x2 - WALL_T;
    const uM = sgn > 0 ? uI2 - AD : uI1 + AD; // portal plane
    const heroY0 = PLINTH + PALACE.HANG_LO;

    const endN = INWARD[end];
    const out = [];

    for (const seg of rectSegments(node, { longStyle: 'cluster', shortStyle: 'cluster' })) {
      // The alcove-end face is excluded from the ordinary treatment and
      // re-hung as heroes lifted by the dais (rectSegments has already split
      // it around any doors the generator put there).
      if (seg.nx === endN[0] && seg.nz === endN[1]) {
        out.push({ ...seg, style: 'hero', y0: heroY0 });
        continue;
      }
      // Side walls: clip the run so no cluster runs behind the portal piers.
      const isSide = alongX ? seg.dx === 1 : seg.dz === 1;
      if (isSide) {
        const start = alongX ? seg.ox : seg.oz;
        let a = start;
        let b = start + seg.len;
        if (sgn > 0) b = Math.min(b, uM - 0.7);
        else a = Math.max(a, uM + 0.7);
        const len = b - a;
        if (len < 1.2) continue;
        out.push({
          ...seg,
          ox: alongX ? a : seg.ox,
          oz: alongX ? seg.oz : a,
          len,
          style: len < 3.2 ? 'hero' : 'cluster',
        });
        continue;
      }
      out.push(seg); // the near end wall, untouched
    }

    // Custom: the two returns inside the alcove, flanking the hero wall.
    const rA = sgn > 0 ? uM + 0.8 : uI1 + 0.4;
    const rB = sgn > 0 ? uI2 - 0.4 : uM - 0.8;
    if (rB - rA >= 1.2) {
      const sides = alongX ? ['N', 'S'] : ['E', 'W'];
      for (const side of sides) {
        const clash = (node.doors || []).some((d0) => {
          if (d0.side !== side) return false;
          const half = (d0.w || PALACE.DOOR_W) / 2 + 0.55;
          return d0.at + half > rA && d0.at - half < rB;
        });
        if (clash) continue;
        const [nx, nz] = INWARD[side];
        const face = side === 'N' ? vI2 : side === 'S' ? vI1 : side === 'E' ? vI2 : vI1;
        out.push({
          ox: alongX ? rA : face,
          oz: alongX ? face : rA,
          dx: alongX ? 1 : 0,
          dz: alongX ? 0 : 1,
          nx,
          nz,
          len: rB - rA,
          style: 'hero',
          y0: heroY0,
        });
      }
    }
    return out.sort((a, b) => b.len - a.len);
  },

  build(node, T) {
    const { WALL_T } = T.PALACE;
    const THREE = T.THREE;
    const end = alcoveEnd(node);
    const alongX = end === 'E' || end === 'W';
    const sgn = end === 'E' || end === 'N' ? 1 : -1;

    const uI1 = alongX ? node.x1 + WALL_T : node.z1 + WALL_T;
    const uI2 = alongX ? node.x2 - WALL_T : node.z2 - WALL_T;
    const vI1 = alongX ? node.z1 + WALL_T : node.x1 + WALL_T;
    const vI2 = alongX ? node.z2 - WALL_T : node.x2 - WALL_T;
    const uM = sgn > 0 ? uI2 - AD : uI1 + AD;
    const alcU1 = sgn > 0 ? uM : uI1;
    const alcU2 = sgn > 0 ? uI2 : uM;
    const hallU1 = sgn > 0 ? uI1 : uM;
    const hallU2 = sgn > 0 ? uM : uI2;

    // (u = long axis, v = cross axis) -> world box / world collider rect.
    const P = (u1, u2, y0, y1, v1, v2) =>
      (alongX ? T.box(u1, u2, y0, y1, v1, v2) : T.box(v1, v2, y0, y1, u1, u2));
    const R = (u1, u2, v1, v2, top) => ({
      rect: alongX
        ? [Math.min(u1, u2), Math.min(v1, v2), Math.max(u1, u2), Math.max(v1, v2)]
        : [Math.min(v1, v2), Math.min(u1, u2), Math.max(v1, v2), Math.max(u1, u2)],
      top,
    });

    const { group, colliders } = T.standardShell(node, T.kit, {
      laylight: false, // built by hand: it belongs over the hall, not centred
      corniceH: 0.5,
    });

    // --- the portal: three receding orders, then a blank screen ----------
    const stoneGeos = [];
    const uB = uM + sgn * FRAME_T;
    for (let k = 0; k < ORDERS; k++) {
      const jw = 0.55 + k * 0.28; // pier width — the opening narrows outward
      const head = 4.9 - k * 0.22; // …and the soffit steps down with it
      const top = CREST - k * 0.18;
      const uA = uM - sgn * k * 0.22;
      stoneGeos.push(
        P(uA, uB, 0, top, vI1, vI1 + jw),
        P(uA, uB, 0, top, vI2 - jw, vI2),
        P(uA, uB, head, top, vI1 + jw, vI2 - jw)
      );
    }
    const jwMax = 0.55 + (ORDERS - 1) * 0.28;
    const uOut = uM - sgn * (ORDERS - 1) * 0.22;
    colliders.push(
      R(uOut, uB, vI1, vI1 + jwMax, CREST),
      R(uOut, uB, vI2 - jwMax, vI2, CREST)
    );

    // Crest cap, proud of every order, and the screen wall above it.
    stoneGeos.push(
      P(uOut - sgn * 0.1, uB, CREST - 0.06, CREST + 0.24, vI1, vI2),
      P(uM, uB, CREST - 0.15, HALL_H, vI1, vI2)
    );

    // Corbel course under the innermost soffit — a nod to muqarnas, all of it
    // well above head height so it needs no collider.
    const openW = vI2 - vI1 - 2 * jwMax;
    const nB = Math.max(3, Math.floor(openW / 0.42));
    const pitch = openW / nB;
    for (let i = 0; i < nB; i++) {
      const v0 = vI1 + jwMax + i * pitch + pitch * 0.16;
      stoneGeos.push(P(uOut - sgn * 0.22, uOut, 4.3, 4.56, v0, v0 + pitch * 0.68));
    }
    group.add(T.mergedMesh(stoneGeos, mat(T.kit, 'stone', 'marble')));

    // --- the dais: low enough to be walkable, so it carries no collider --
    group.add(T.mergedMesh(
      [
        P(alcU1, alcU2, -0.08, PLINTH, vI1, vI2),
        P(uM, uM - sgn * 0.55, -0.08, PLINTH * 0.5, vI1, vI2), // broad step
      ],
      mat(T.kit, 'terracotta', 'marble')
    ));

    // --- lighting: a long coffer over the hall, a low one over the alcove -
    const coffer = (u1, u2, v1, v2, y) => {
      const B = 0.16;
      const w1 = alongX ? u1 : v1;
      const w2 = alongX ? u2 : v2;
      const d1 = alongX ? v1 : u1;
      const d2 = alongX ? v2 : u2;
      const trim = [
        T.box(w1 - B, w2 + B, y - 0.16, y, d1 - B, d1),
        T.box(w1 - B, w2 + B, y - 0.16, y, d2, d2 + B),
        T.box(w1 - B, w1, y - 0.16, y, d1, d2),
        T.box(w2, w2 + B, y - 0.16, y, d1, d2),
      ];
      const mesh = new THREE.Mesh(T.kit.geometries.unitPlane, T.kit.materials.laylight);
      mesh.rotation.x = Math.PI / 2;
      mesh.scale.set(w2 - w1, d2 - d1, 1);
      mesh.position.set((w1 + w2) / 2, y - 0.02, (d1 + d2) / 2);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      return { trim, mesh };
    };

    const hall = coffer(hallU1 + 1.0, hallU2 - 1.0, vI1 + 1.6, vI2 - 1.6, HALL_H);
    const alc = coffer(alcU1 + 0.45, alcU2 - 0.45, vI1 + 0.7, vI2 - 0.7, 5.6);
    group.add(hall.mesh, alc.mesh);

    // The alcove soffit closes it off low; the screen above hides the void.
    group.add(T.mergedMesh(
      [P(alcU1, alcU2, 5.6, 5.86, vI1, vI2)],
      T.kit.materials.ceiling
    ));
    group.add(T.mergedMesh([...hall.trim, ...alc.trim], T.kit.materials.trim));

    // Benches in the hall, set against the side walls clear of every door.
    const bu = (hallU1 + hallU2) / 2;
    for (const s of [1, -1]) {
      const bv = s > 0 ? vI2 - 0.75 : vI1 + 0.75;
      const blocked = (node.doors || []).some((d0) => {
        const face = alongX ? (s > 0 ? 'N' : 'S') : (s > 0 ? 'E' : 'W');
        return d0.side === face && Math.abs(d0.at - bu) < (d0.w || T.PALACE.DOOR_W) / 2 + 1.6;
      });
      if (blocked) continue;
      const b = T.bench(
        T.kit,
        alongX ? bu : bv,
        alongX ? bv : bu,
        alongX ? 0 : Math.PI / 2,
        2.4
      );
      group.add(b.mesh);
      colliders.push(b.collider);
    }

    return { group, colliders };
  },
};

export default iwan;
